/* ============================================================================
   16 · Embroidery — Personalize Your FIGS, on its own page.
   Source: the claude.ai embroidery artifact (F1hsVacSPjDUHf7HJtp8U3; merge
   base docs/embroidery-merge-base/), built off Figma MVP Embroidery
   1622:112110 / mobile 1936:108889, with its completed state from the PDP
   Embroidery section 1163:97123. Everything embroidery lives in the shared
   module (src/figs-embroidery/) — the Catarina PDP and the set's kit drawer
   open the same configurator — so this page is only the stage around it.
   ----------------------------------------------------------------------------
   What's being decided here:

     • The configurator on its own, at full size, without a PDP around it:
       placements, the per-garment allowances, pricing, the live preview and
       the motion inside the modal. It opens as the page arrives.
     • The completed state the artifact didn't have: I'M DONE closes the
       modal onto EMBROIDERY ADDED (PDP/Embroidery - Added 1163:96298) — the
       stage as its thumbnail, the total, SHOW SUMMARY listing the charges by
       kind (Added, Expanded 2667:185680), EDIT back into the configurator,
       REMOVE — and the plate beside it wears the embroidery.

   The garment is the artifact's navy Catarina render. On the PDP the same
   stage takes the page's colour.
   Reduced motion is a design state: the modal and card crossfade, nothing
   travels, the sample mark shows finished.
   ========================================================================== */

import { mountFigsNav } from '/src/figs-nav/figs-nav.js';
import { createFigsEmbroidery, swapSlot } from '/src/figs-embroidery/figs-embroidery.js';
import {
  animate, MO, state, tween, play, teardown, setSpeed, clearTracked, $, $$, mqDesktop,
} from '../10-pdp-prototype/mo.js';

mountFigsNav({
  promo: $('[data-fnav-promo]'),
  masthead: $('[data-fnav-masthead]'),
  footer: $('[data-fnav-footer]'),
  theme: 'light',
  homeHref: '/experiments/08-d-homepage/',
});

/* ============================================================
   EMBROIDERY — one configurator; the entry, the card and the
   plate all read what I'M DONE attached
   ============================================================ */
const addEl = $('[data-emb-open]');
const addedEl = $('[data-emb-added]');
const live = document.createElement('p');
live.className = 'femb-sr';
live.setAttribute('aria-live', 'polite');
document.body.append(live);
const say = (t) => { live.textContent = ''; requestAnimationFrame(() => { live.textContent = t; }); };

const emb = createFigsEmbroidery({
  onChange(attached, { source }) {
    // signed in (the nav's log-in flow), the card holds the slot even with
    // nothing attached: the saved preferences, ADD TO PRODUCT · EDIT
    swap(emb.showsCard());
    if (source === 'done' && attached.size) {
      say(`Embroidery added. Total $${emb.total()}.`);
      settlePlate();
    } else if (!attached.size && source !== 'account') {
      say('Embroidery removed.');
    }
  },
});

/* the entry and EMBROIDERY ADDED share one slot (swapSlot, the PDP's) */
function swap(has) {
  if (has === !addedEl.hidden) return;
  if (has) swapSlot(addEl, addedEl);
  else swapSlot(addedEl, addEl);
}

/* the plate takes the embroidery the way the PDP's does when it arrives:
   a settle from 1.04 on --mo-cinematic */
function settlePlate() {
  tween($('.femb-plate'), { scale: [MO.scaleIn, 1] }, { duration: MO.cinematic, ease: MO.eEntrance });
}

emb.preview($('[data-emb-preview]'), { fit: 0.725 });
emb.card(addedEl, { onEdit: (btn) => emb.open(btn) });
if (emb.showsCard()) { addEl.hidden = true; addedEl.hidden = false; addedEl.setAttribute('data-intro', ''); }
addEl.addEventListener('click', (e) => { e.preventDefault(); emb.open(addEl); });

/* ============================================================
   FIRST SCREEN — the PDP's recipe: the plate fades on
   --mo-deliberate and settles from 1.04 on --mo-cinematic, the
   rail rises 16px on --mo-slow behind it. Then the configurator
   opens, as the artifact presents it.
   ============================================================ */
const INTRO = 0.12;
let openTimer = 0;
function boot() {
  document.documentElement.dataset.reduced = String(state.reduced);
  const plate = $('[data-intro-plate]');
  tween(plate, { opacity: [0, 1] }, { duration: MO.deliberate, ease: MO.eEntrance, delay: INTRO });
  tween($('.femb-plate', plate), { scale: [MO.scaleIn, 1] }, { duration: MO.cinematic, ease: MO.eEntrance, delay: INTRO });
  tween($$('.emb-page__rail [data-intro]'), { y: [MO.riseSm, 0], opacity: [0, 1] },
    { duration: MO.slow, ease: MO.eEntrance, delay: INTRO + 0.16 });
  clearTimeout(openTimer);
  openTimer = setTimeout(() => { if (!emb.isOpen && !emb.has()) emb.open(addEl); }, state.reduced ? 0 : 650 / state.speed);
}

/* ============================================================
   LAB BAR — the PDP's, shipped hidden (H brings it back)
   ============================================================ */
function initLabBar() {
  const bar = $('[data-lab-bar]');
  const handle = $('[data-lab-handle]');
  const slider = $('#lab-speed');
  const readout = $('[data-readout]');
  const syncOffset = () => {
    const h = !bar.isConnected || bar.hidden || mqDesktop.matches ? 0 : bar.offsetHeight + 8;
    document.documentElement.style.setProperty('--lab-offset', `${h}px`);
  };
  slider.addEventListener('input', () => {
    const v = parseFloat(slider.value);
    readout.textContent = `${v.toFixed(2)}x`;
    setSpeed(v);
  });
  const rebuild = async () => {
    await emb.close();
    emb.clear();
    window.scrollTo({ top: 0, behavior: 'instant' });
    teardown.splice(0).forEach((fn) => { try { fn?.(); } catch (e) { /* ignore */ } });
    clearTracked();
    const pre = $$('[data-intro]');
    pre.forEach((el) => { el.style.opacity = '0'; });
    animate(pre, { opacity: 0 }, { duration: 0 });
    requestAnimationFrame(boot);
  };
  $('[data-replay]').addEventListener('click', rebuild);
  const rb = $('[data-reduced-btn]');
  rb.setAttribute('aria-pressed', String(state.reduced));
  rb.addEventListener('click', () => {
    state.reduced = !state.reduced;
    rb.setAttribute('aria-pressed', String(state.reduced));
    rebuild();
  });
  const gb = $('[data-grid-btn]');
  const ov = $('[data-grid-overlay]');
  gb.addEventListener('click', () => {
    const on = gb.getAttribute('aria-pressed') !== 'true';
    gb.setAttribute('aria-pressed', String(on));
    ov.dataset.on = String(on);
  });
  let killed = false;
  $('[data-hide-btn]').addEventListener('click', () => {
    killed = true;
    handle.hidden = true;
    bar.remove();
    syncOffset();
  });
  addEventListener('keydown', (e) => {
    if (killed) return;
    if (e.key.toLowerCase() !== 'h' || e.metaKey || e.ctrlKey || e.altKey) return;
    if (/^(input|textarea|select)$/i.test(e.target.tagName)) return;
    const hide = !bar.hidden;
    bar.hidden = hide;
    handle.hidden = !hide;
    syncOffset();
  });
  handle.addEventListener('click', () => {
    if (killed) return;
    bar.hidden = false;
    handle.hidden = true;
    syncOffset();
  });
  addEventListener('resize', syncOffset);
  mqDesktop.addEventListener('change', syncOffset);
  syncOffset();
  tween(bar, { y: [MO.riseMd, 0], opacity: [0, 1] }, { duration: MO.slow, delay: 0.4, ease: MO.eEntrance });
}

boot();
/* tells the head safety net the reveals are running */
document.documentElement.setAttribute('data-mo-ready', '');
initLabBar();
console.info('%cFIGS Motion Lab · 16 Embroidery', 'font-weight:700', '\nMotion: motion.dev v12 (vanilla)', '\nReduced:', state.reduced);
